import {
  FloatingFocusManager,
  FloatingOverlay,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import clsx from 'clsx';
import { memo, useId } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Action } from '../../types/Action';
import { type HeaderProps } from '../../types/SharedHeaderProps';
import './Modal.css';
import { Card } from '../Card/Card';
import { Portal } from './subcomponents/Portal';
import { ModalProps } from './types';

const messages = defineMessages({
  closeActionLabel: {
    id: 'Modal.closeButton',
    defaultMessage: 'Close',
    description: 'Label for the action to close the modal',
  },
});

export const Modal = memo(
  ({
    children,
    footer,
    header,
    isContentFullBleed,
    isVisible,
    onHide,
    size = 'm',
  }: ModalProps) => {
    const intl = useIntl();
    const { refs, context } = useFloating({
      open: isVisible,
      onOpenChange: (open) => {
        if (!open) onHide();
      },
    });

    const role = useRole(context, { role: 'dialog' });
    // A takeover leaves no page to click back to, so only the sizes that show
    // one dismiss from outside the dialog.
    const dismiss = useDismiss(context, {
      outsidePress: size !== 'full-page',
    });

    const { getFloatingProps } = useInteractions([role, dismiss]);

    const subtitleId = useId();
    const titleId = useId();

    if (!isVisible) {
      return null;
    }

    const closeAction: Action = {
      icon: { key: 'x' },
      label: intl.formatMessage(messages.closeActionLabel),
      text: intl.formatMessage(messages.closeActionLabel),
      onClick: onHide,
    };

    return (
      <Portal>
        <FloatingOverlay lockScroll className="eds-modal--overlay">
          <FloatingFocusManager context={context}>
            <div
              {...getFloatingProps()}
              aria-describedby={header.subtitle ? subtitleId : undefined}
              aria-labelledby={titleId}
              aria-modal="true"
              className={clsx('eds-modal', `eds-modal--size-${size}`)}
              ref={refs.setFloating}
            >
              <Card
                footer={footer}
                header={
                  {
                    ...header,
                    closeAction,
                    subtitleId,
                    titleId,
                  } as HeaderProps
                }
                // Only a takeover carries the larger chrome; a dialog that
                // leaves the page visible reads as part of it.
                size={size === 'full-page' ? 'l' : 'm'}
                __private={{ disableContentPadding: isContentFullBleed }}
              >
                {children}
              </Card>
            </div>
          </FloatingFocusManager>
        </FloatingOverlay>
      </Portal>
    );
  },
);

Modal.displayName = 'Modal';
